import TagManager from 'react-gtm-module';
import Config from 'arbitrip-common/client/utils/Config';
import moment from 'moment';
import { mapEventToGA4Ecommerce, toBrandNeutralFunnelEvent } from './ga4-ecommerce';
import { mapEventToElalDataLayer, getElalPayloadKind, ELAL_PAYLOAD_KIND } from './elal-events';

// Keep legacy custom GTM events disabled by default.
const ENABLE_NON_GA4_GTM_EVENTS = false;

// Dedicated dataLayer for containers that must not see brand-specific fields. GTM reads only the
// dataLayer it was loaded with (gtm.js?id=...&l=<name>), so this isolates them from window.dataLayer.
const GENERIC_DATA_LAYER_NAME = 'genericDataLayer';

let gtmInitialized = false;
let genericGtmInitialized = false;

const elalPushedKeys = new Set();
let elalPageKeys = new Set();

// GTM keeps every pushed key in its data model, so a field from an earlier ELAL event (popup_name,
// step_in_funnel, ecommerce...) would otherwise still be readable when a later event fires.
function withStaleElalKeysCleared(elalEvent) {
    const cleared = {};
    elalPushedKeys.forEach((key) => {
        if (!(key in elalEvent) && !elalPageKeys.has(key)) cleared[key] = undefined;
    });
    Object.keys(elalEvent).forEach((key) => elalPushedKeys.add(key));
    return { ...cleared, ...elalEvent };
}

// Page context outlives events but not the page: keys only the previous page had are reset.
function withStalePageKeysCleared(elalPage) {
    const cleared = {};
    elalPageKeys.forEach((key) => {
        if (!(key in elalPage)) cleared[key] = undefined;
    });
    elalPageKeys = new Set(Object.keys(elalPage));
    return { ...cleared, ...elalPage };
}

function pushElalPayload(payload) {
    const kind = getElalPayloadKind(payload);
    if (kind === ELAL_PAYLOAD_KIND.PAGE) {
        window.dataLayer.push(withStalePageKeysCleared(payload));
        return;
    }
    if (kind === ELAL_PAYLOAD_KIND.USER_DATA) {
        window.dataLayer.push(payload);
        return;
    }
    // GTM merges ecommerce objects across pushes; clear it so items never leak between events.
    if (payload.ecommerce) {
        window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(withStaleElalKeysCleared(payload));
}

function isElalMobileWebview() {
    return typeof navigator !== 'undefined' && navigator.userAgent.includes('ElalMobile');
}

function resolveGtmIdByHostname(gtmIdsPerDomain, hostname) {
    if (!gtmIdsPerDomain || !hostname) {
        return null;
    }

    for (const [domain, gtmId] of gtmIdsPerDomain) {
        if (hostname === domain || hostname.endsWith('.' + domain)) {
            return gtmId;
        }
    }
    return null;
}

export function startSession(profile) {
    if (isElalMobileWebview()) return;
    if (!Config.gtmId) {
        return;
    }
    if (gtmInitialized) return;

    try {
        const tagManagerArgs = {
            gtmId: Config.gtmId,
        };

        TagManager.initialize(tagManagerArgs);

        // Initialize additional container for specific domain
        const gtmId = Config.additionalGTMperDomain.get(window.location.hostname);

        if (gtmId) {
            const additionalTagManagerArgs = {
                gtmId,
            };
            TagManager.initialize(additionalTagManagerArgs);
        }

        // Initialize brand-neutral container for specific domain on its own dataLayer
        const genericGtmId = resolveGtmIdByHostname(Config.genericGTMperDomain, window.location.hostname);

        if (genericGtmId) {
            TagManager.initialize({
                gtmId: genericGtmId,
                dataLayerName: GENERIC_DATA_LAYER_NAME,
            });
            genericGtmInitialized = true;
        }

        gtmInitialized = true;
    } catch (_err) {
        // intentionally ignored
    }
}

export function trackEvent(event_name, properties, callback) {
    try {
        if (isElalMobileWebview()) return;
        const safeProperties = fixFormats(properties);

        if (typeof window === 'undefined') return;

        window.dataLayer = window.dataLayer || [];

        // Legacy/custom GTM event payload (non-GA4)
        const standardEventPayload = {
            event: event_name,
            ...safeProperties,
        };

        if (ENABLE_NON_GA4_GTM_EVENTS) {
            window.dataLayer.push(standardEventPayload);
        }

        // Check if this event should also be sent as GA4 ecommerce
        let ga4Event = null;
        try {
            ga4Event = mapEventToGA4Ecommerce(event_name, safeProperties);
        } catch (_err) {
            // intentionally ignored
        }

        if (ga4Event) {
            window.dataLayer.push(ga4Event);

            if (genericGtmInitialized) {
                window[GENERIC_DATA_LAYER_NAME] = window[GENERIC_DATA_LAYER_NAME] || [];
                window[GENERIC_DATA_LAYER_NAME].push(toBrandNeutralFunnelEvent(ga4Event));
            }
        }

        if (Config.is_elal) {
            let elalEvent = null;
            try {
                elalEvent = mapEventToElalDataLayer(event_name, safeProperties);
            } catch (_err) {
                // intentionally ignored
            }

            if (elalEvent) {
                pushElalPayload(elalEvent);
            }
        }
    } catch (_err) {
        // intentionally ignored
    } finally {
        if (typeof callback === 'function') {
            try {
                callback();
            } catch (_err) {
                // intentionally ignored
            }
        }
    }
}

export function endSession(profile) {
    return undefined;
}

function fixFormats(data) {
    if (!data || typeof data !== 'object') {
        return {};
    }

    let payload = {};
    for (const prop in data) {
        let value = data[prop];
        ///
        /// convert date to window.mixpanel date format
        ///
        if ((value instanceof Date || value instanceof moment) && moment(value).isValid(value)) {
            value = moment.utc(moment(value).format('YYYY-MM-DD')).toISOString().split('.')[0];
        }
        payload[prop] = value;
    }
    return payload;
}
